<!--
  SupportCommunityHelp.svelte — where a user without a support licence can still
  get help: the Abstract community and the Abstract Discord.

  Support is a licence entitlement, so most people who reach the support pages
  will be told they cannot open a ticket. That answer alone is a dead end; this
  is the way out of it. Each link is shown only when an administrator configured
  it (Settings → Admin → Support), and with neither the block renders nothing.
-->
<script lang="ts">
	import type { SupportCommunityLinks } from '../AppTypes';
	import { t } from '../i18n';

	interface Props {
		/** The configured links, or `null` while they are unknown. */
		links: SupportCommunityLinks | null;
		/** Extra classes for the outer block. */
		class?: string;
	}

	let { links, class: className = '' }: Props = $props();

	/** Whether there is anywhere to send the user. */
	let hasLinks = $derived(!!links && (!!links.communityURL || !!links.discordURL));
</script>

{#if hasLinks && links}
	<!-- The explanation on top and the ways out below it as real buttons, full
	     width on a phone: for someone just told they cannot open a ticket, these
	     links are the answer, not a footnote to it. -->
	<div class="widget space-y-3 {className}" data-testid="support-community-help">
		<div class="flex items-start gap-3">
			<i class="fa-solid fa-people-group text-default mt-0.5 text-xl" aria-hidden="true"></i>
			<div class="min-w-0 flex-1">
				<p class="font-semibold text-gray-900 dark:text-white">{$t('No access to support?')}</p>
				<p class="text-default text-sm">
					{$t('Consider purchasing a license to get instant access to our in-house engineering team. Otherwise, you can get help from the Abstract community and on the Abstract Discord.')}
				</p>
			</div>
		</div>
		<div class="flex flex-col gap-2 sm:flex-row">
			{#if links.communityURL}
				<a
					href={links.communityURL}
					target="_blank"
					rel="noopener noreferrer"
					class="bg-primary-600 hover:bg-primary-700 focus-visible:ring-primary-300 dark:focus-visible:ring-primary-800 inline-flex items-center justify-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition focus-visible:ring-4 focus-visible:outline-none"
				>
					<i class="fa-solid fa-comments" aria-hidden="true"></i>{$t('Abstract Community')}
				</a>
			{/if}
			{#if links.discordURL}
				<!-- Discord's own blurple, so the button is recognisable before it is read. -->
				<a
					href={links.discordURL}
					target="_blank"
					rel="noopener noreferrer"
					class="inline-flex items-center justify-center gap-2 rounded-lg bg-[#5865F2] px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#4752C4] focus-visible:ring-4 focus-visible:ring-[#5865F2]/40 focus-visible:outline-none"
				>
					<i class="fa-brands fa-discord" aria-hidden="true"></i>{$t('Abstract Discord')}
				</a>
			{/if}
		</div>
	</div>
{/if}
